/**
 * AdminManualsEditor — in-browser editor for the per-(role, lang) JSON
 * translation files at `/app/backend/manuals/`. Two-pane layout: file
 * list on the left, JSON editor on the right with live-validated save.
 *
 * Workflow:
 *   1. Pick a file → fetched + pretty-printed into a textarea.
 *   2. Edit JSON inline.
 *   3. "Save" → parses client-side first (instant feedback), then
 *      PUTs the full content back to the backend which re-validates
 *      server-side and atomically replaces the file.
 *   4. Backend's `mtime` cache busts automatically on next manual load.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, FloppyDisk, ArrowsClockwise, Warning,
  CheckCircle, FileText,
} from "@phosphor-icons/react";
import { fetchManualFiles, fetchManualFile, saveManualFile } from "@/lib/api";

const LANG_LABEL = { en: "English", fr: "Français", es: "Español" };

export default function AdminManualsEditor({ user }) {
  const [files, setFiles] = useState(null);
  const [activeName, setActiveName] = useState("");
  const [activeContent, setActiveContent] = useState(null);
  const [text, setText] = useState("");
  const [origText, setOrigText] = useState("");
  const [loadingFile, setLoadingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parseErr, setParseErr] = useState("");
  const [saveErr, setSaveErr] = useState("");

  const dirty = text !== origText;

  // Load file list once.
  const loadList = () => fetchManualFiles().then(setFiles).catch((e) => {
    setFiles({ files: [], base_dir: "" });
    toast.error(e?.response?.data?.detail || "Could not load manuals list");
  });
  useEffect(() => { loadList(); }, []);

  // Group files by lang for the sidebar.
  const grouped = useMemo(() => {
    const out = { en: [], fr: [], es: [] };
    for (const f of files?.files || []) {
      (out[f.lang] ||= []).push(f);
    }
    return out;
  }, [files]);

  const openFile = async (name) => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    setActiveName(name); setLoadingFile(true);
    setParseErr(""); setSaveErr("");
    try {
      const data = await fetchManualFile(name);
      const pretty = JSON.stringify(data.content, null, 2);
      setActiveContent(data); setText(pretty); setOrigText(pretty);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load file");
    } finally { setLoadingFile(false); }
  };

  const handleTextChange = (v) => {
    setText(v);
    setSaveErr("");
    try {
      JSON.parse(v);
      setParseErr("");
    } catch (e) {
      setParseErr(e.message);
    }
  };

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(text); }
    catch (e) { setParseErr(e.message); toast.error("JSON parse error — fix syntax first"); return; }
    setSaving(true); setSaveErr("");
    try {
      const r = await saveManualFile(activeName, parsed);
      setOrigText(text);
      toast.success(`Saved · ${r.section_count} sections · ${r.bytes} bytes`);
      loadList(); // refresh updated_at on the sidebar
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      setSaveErr(msg); toast.error(msg);
    } finally { setSaving(false); }
  };

  const reset = () => {
    if (!dirty) return;
    if (!window.confirm("Revert to last-saved content?")) return;
    setText(origText); setParseErr(""); setSaveErr("");
  };

  if (!files) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500"><ArrowsClockwise size={14} className="animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sticky top-0 z-10"
              data-testid="admin-manuals-header">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <Link to="/" data-testid="admin-manuals-back" className="text-slate-500 hover:text-slate-800">
            <ArrowLeft size={18} />
          </Link>
          <BookOpen size={16} weight="bold" className="text-blue-700" />
          <h1 className="text-sm font-bold font-['Chivo'] tracking-tight">
            Manual translations editor
          </h1>
          <span className="text-[11px] text-slate-500 font-mono ml-2">{files.base_dir}</span>
          <div className="ml-auto text-xs text-slate-500 truncate max-w-[200px]">{user?.email}</div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4">
        {/* Sidebar */}
        <aside className="bg-white border border-slate-200 rounded-md p-2 max-h-[calc(100vh-140px)] overflow-y-auto"
               data-testid="admin-manuals-sidebar">
          {Object.entries(grouped).map(([lang, list]) => (
            <div key={lang} className="mb-3">
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500 px-2 pt-2 pb-1">
                {LANG_LABEL[lang] || lang.toUpperCase()} · {list.length}
              </div>
              <ul className="space-y-0.5">
                {list.map((f) => (
                  <li key={f.name}>
                    <button onClick={() => openFile(f.name)}
                            data-testid={`admin-manuals-file-${f.name}`}
                            className={`w-full text-left px-2 py-1.5 rounded-sm flex items-start gap-2 ${
                              activeName === f.name
                                ? "bg-blue-50 border border-blue-200"
                                : "hover:bg-slate-50 border border-transparent"
                            }`}>
                      <FileText size={11} weight="bold" className={activeName === f.name ? "text-blue-700 mt-0.5" : "text-slate-400 mt-0.5"} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono truncate">{f.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {f.section_count} sections · {(f.bytes / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>

        {/* Editor */}
        <section className="bg-white border border-slate-200 rounded-md flex flex-col min-h-[60vh]"
                 data-testid="admin-manuals-editor">
          {!activeName ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-12 text-slate-500">
              <BookOpen size={32} className="mb-3 text-slate-300" />
              <p className="text-sm">Pick a file from the sidebar to start editing.</p>
              <p className="text-[11px] mt-2 max-w-md">
                These are the live translation files served by <code className="font-mono bg-slate-100 px-1">/api/manual</code>.
                Hot-reload picks up changes on the next request — no backend restart required.
              </p>
            </div>
          ) : loadingFile ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
              <ArrowsClockwise size={14} className="animate-spin mr-2" /> Loading {activeName}…
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                <FileText size={14} weight="bold" className="text-blue-700" />
                <h2 className="text-sm font-bold font-mono" data-testid="admin-manuals-active">{activeName}</h2>
                <span className="text-[10px] text-slate-500">
                  {activeContent?.role} · {LANG_LABEL[activeContent?.lang] || activeContent?.lang}
                </span>
                {dirty && (
                  <span data-testid="admin-manuals-dirty"
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
                    Unsaved
                  </span>
                )}
                {parseErr && (
                  <span className="text-[10px] text-red-700 inline-flex items-center gap-1"
                        data-testid="admin-manuals-parse-err">
                    <Warning size={10} weight="fill" /> {parseErr}
                  </span>
                )}
                <div className="ml-auto flex gap-2">
                  <button onClick={reset} disabled={!dirty}
                          data-testid="admin-manuals-revert"
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-300 hover:bg-slate-50 disabled:opacity-40">
                    Revert
                  </button>
                  <button onClick={save} disabled={!dirty || !!parseErr || saving}
                          data-testid="admin-manuals-save"
                          className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                    {saving ? <ArrowsClockwise size={11} className="animate-spin" /> : <FloppyDisk size={11} weight="bold" />}
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>

              {saveErr && (
                <div className="mx-4 mt-3 p-2 rounded-sm bg-red-50 border border-red-200 text-xs text-red-800"
                     data-testid="admin-manuals-save-err">
                  <Warning size={11} weight="fill" className="inline mr-1" />
                  {saveErr}
                </div>
              )}

              <textarea
                data-testid="admin-manuals-textarea"
                value={text}
                onChange={(e) => handleTextChange(e.target.value)}
                spellCheck={false}
                className="flex-1 w-full p-3 font-mono text-xs leading-relaxed resize-none focus:outline-none"
                style={{ minHeight: "62vh" }}
              />

              <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                <CheckCircle size={10} weight="fill" className={parseErr ? "text-slate-300" : "text-emerald-600"} />
                <span>JSON {parseErr ? "invalid" : "valid"}</span>
                <span className="ml-auto">
                  Last saved: {(activeContent?.updated_at || "").slice(0, 19).replace("T", " ")} UTC
                </span>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
